/**
 * Progress Share Modal
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ShareIcon, SparklesIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ProgressShareModal = ({ isOpen, onClose, entityType, entityId, entityData, user }) => {
  const [milestone, setMilestone] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [includeExactValues, setIncludeExactValues] = useState(false);
  const [exactValues, setExactValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [auraContext, setAuraContext] = useState('');

  useEffect(() => {
    if (isOpen && entityData) {
      generateProgressData();
      fetchAuraContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityData]);

  const generateProgressData = () => {
    if (!entityData) return;

    let generatedMilestone = '';
    let generatedSummary = '';
    let generatedValues = {};

    switch (entityType) {
      case 'vision':
        const progress = entityData.current_amount / entityData.target_amount * 100;
        generatedMilestone = `${Math.round(progress)}% complete`;
        generatedSummary = `Made progress on "${entityData.title}"!`;
        generatedValues = {
          current: entityData.current_amount,
          target: entityData.target_amount,
          progress: progress
        };
        break;
      
      case 'net_worth':
        generatedMilestone = `Net worth milestone reached`;
        generatedSummary = `Reached a new net worth milestone!`;
        generatedValues = {
          net_worth: entityData.current_net_worth
        };
        break;
      
      case 'learning':
        generatedMilestone = `Completed: ${entityData.node_title || 'Learning node'}`;
        generatedSummary = `Just completed a learning module!`;
        generatedValues = {
          node_id: entityData.node_id,
          category: entityData.category
        };
        break;
      
      case 'game':
        generatedMilestone = entityData.achievement || 'Mission completed';
        generatedSummary = `Completed a Risk Gym mission!`;
        generatedValues = {
          mission_id: entityData.mission_id,
          xp_earned: entityData.xp_earned
        };
        break;
      default:
        generatedMilestone = 'Progress milestone';
        generatedSummary = 'Made progress on a goal!';
        break;
    }

    setMilestone(generatedMilestone);
    setSummary(generatedSummary);
    setExactValues(generatedValues);
  };

  const fetchAuraContext = async () => {
    if (!summary) return;
    
    try {
      const response = await axios.post('/api/platform/aura/progress-context', null, {
        params: {
          progress_summary: summary,
          entity_type: entityType
        }
      });
      setAuraContext(response.data.data?.context || '');
    } catch (err) {
      console.error('Error fetching Aura context:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!milestone || !summary) return;

    try {
      setLoading(true);
      const payload = {
        entity_type: entityType,
        entity_id: entityId,
        milestone: milestone,
        summary: summary,
        include_exact_values: includeExactValues,
        content: content || undefined
      };

      if (includeExactValues && Object.keys(exactValues).length > 0) {
        payload.exact_values = exactValues;
      }

      await axios.post('/api/platform/posts/share-progress', payload);
      onClose();
      if (window.dispatchEvent) {
        window.dispatchEvent(new Event('progressShared'));
      }
    } catch (err) {
      console.error('Error sharing progress:', err);
      alert('Failed to share progress');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="modal-overlay">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          className="surface-panel max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-hairline">
            <div className="flex items-center gap-2">
              <ShareIcon className="w-5 h-5 text-pine" />
              <span className="text-primary font-display font-semibold">Share Progress</span>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost p-1"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {auraContext && (
              <div className="p-3 bg-pine-tint border border-hairline rounded-lg">
                <p className="text-sm text-secondary">
                  <span className="text-pine font-medium flex items-center gap-1">
                    <SparklesIcon className="w-4 h-4" /> Aura:
                  </span>{' '}
                  {auraContext}
                </p>
              </div>
            )}

            <div>
              <label className="block text-primary font-medium mb-2">Milestone</label>
              <input
                type="text"
                value={milestone}
                onChange={(e) => setMilestone(e.target.value)}
                placeholder="e.g., 50% complete"
                className="input-editorial"
                required
              />
            </div>

            <div>
              <label className="block text-primary font-medium mb-2">Summary</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Describe your progress..."
                rows={3}
                className="input-editorial resize-none"
                required
              />
            </div>

            <div>
              <label className="block text-primary font-medium mb-2">Additional Message (Optional)</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Add any thoughts..."
                rows={2}
                className="input-editorial resize-none"
              />
            </div>

            <div className="flex items-center gap-3 p-4 bg-pine-tint border border-hairline rounded-lg">
              <input
                type="checkbox"
                id="includeExactValues"
                checked={includeExactValues}
                onChange={(e) => setIncludeExactValues(e.target.checked)}
                className="checkbox-editorial"
              />
              <label htmlFor="includeExactValues" className="text-secondary text-sm">
                Share exact values (amounts, numbers) - default is summary only for privacy
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !milestone || !summary}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sharing...' : 'Share to Feed'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProgressShareModal;
